/**
 * Extraction des participants depuis un email collé.
 *
 * Le client envoie la liste de ses élus par email, sous des formes très
 * variables : une par ligne, séparées par des virgules, avec ou sans
 * civilité, le nom avant ou après le prénom. Les ressaisir un à un est long
 * et fautif ; on lit l'email à leur place.
 *
 * L'ANCRE est l'adresse email : c'est le seul élément dont la forme est
 * sûre. Une ligne sans adresse est ignorée — deviner un participant à partir
 * d'un nom isolé ferait entrer les formules de politesse et les signatures
 * dans la liste. Le reste de la ligne fournit le nom, la civilité et la
 * qualité quand ils y sont.
 *
 * Le résultat est une proposition : elle remplit le formulaire, où elle se
 * corrige avant enregistrement. Rien n'est écrit en base ici.
 */

const RE_EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const RE_EMAIL_TOUTES = new RegExp(RE_EMAIL.source, 'g');

/** Étiquettes d'entête à retirer avant lecture d'une ligne. */
const RE_ENTETE = /^\s*(de|à|a|cc|cci|pour|to|from|objet|subject|envoyé|sent)\s*:\s*/i;

const CIVILITES = [
  { motif: /\b(madame|mme)\b\.?/i, valeur: 'Madame' },
  { motif: /\b(monsieur|mr|m)\b\.?/i, valeur: 'Monsieur' },
];

/**
 * Qualités reconnues, les plus longues d'abord : « Secrétaire adjoint » doit
 * l'emporter sur « Secrétaire ».
 */
const QUALITES = [
  // Celui qui anime figure souvent dans le même mail que les élus : reconnu
  // ici, il est mis à part par le dossier direct (dossierDepuisExtraction)
  // plutôt que convoqué comme stagiaire.
  'Formatrice',
  'Formateur',
  'Intervenante',
  'Intervenant',
  'Référente harcèlement',
  'Référent harcèlement',
  'Secrétaire adjointe',
  'Secrétaire adjoint',
  'Trésorière adjointe',
  'Trésorier adjoint',
  'Représentant de proximité',
  'Secrétaire',
  'Trésorière',
  'Trésorier',
  'Présidente',
  'Président',
  'Référente',
  'Référent',
  'Suppléante',
  'Suppléant',
  'Titulaire',
  'Membre CSE',
  'Élue',
  'Élu',
];

const QUALITE_DEFAUT = 'Membre CSE';

/* ---------- L'entreprise de la personne ---------- */

/** Mention explicite : « société ACME », « entreprise : ACME », « chez ACME ». */
const RE_MARQUEUR_SOCIETE =
  /\b(?:soci[ée]t[ée]|entreprise|employeur|ste|chez)\s*:?\s*([^\t,;|()<>\n]+?)\s*(?=$|[\t,;|()<>]|\s[-–—]\s)/i;

/** Entre parenthèses : « Marie DUPONT (ACME) ». */
const RE_PARENTHESES = /\(([^()]+)\)/g;

/** Colonnes d'une ligne : tabulation, barre verticale, point-virgule, ou tiret entouré d'espaces. */
const RE_COLONNES = /\t+|\s*[|;]\s*|\s+[-–—]\s+/;

/** Ce qui ouvre une phrase et non un nom d'entreprise : « du CSE », « pour le suivi »… */
const RE_MOT_OUTIL = /^(du|de|des|d'|la|le|les|au|aux|pour|et|en|sur|chez|avec)\b/i;

/**
 * Une entreprise plausible, ou '' : ni trop courte, ni une phrase. La lecture
 * est une PROPOSITION relue avant enregistrement, mais mieux vaut laisser le
 * champ vide que le remplir d'un morceau de phrase.
 */
function nettoyerSociete(texte) {
  const t = String(texte || '')
    .replace(/\s+/g, ' ')
    .replace(/^[\s:–—-]+|[\s.:–—-]+$/g, '')
    .trim();
  if (t.length < 2 || t.length > 80 || !/\p{L}/u.test(t) || RE_MOT_OUTIL.test(t)) return '';
  return t;
}

/** Plafond de sécurité : un email collé n'apporte pas cent participants. */
const MAX_PARTICIPANTS = 100;

/** Échappe ce qui doit entrer littéralement dans une expression régulière. */
const echapper = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** « jean-pierre » -> « Jean-Pierre » ; les traits internes sont conservés. */
function capitaliser(mot) {
  return mot
    .toLocaleLowerCase('fr')
    .split(/([-'’])/)
    .map((p) => (/[-'’]/.test(p) ? p : p.charAt(0).toLocaleUpperCase('fr') + p.slice(1)))
    .join('');
}

/** Un jeton n'est un fragment de nom que s'il porte au moins une lettre. */
const estMot = (t) => /\p{L}/u.test(t);

/** Majuscules uniquement : la convention française du nom de famille. */
const estCapitales = (t) => t.length >= 2 && t === t.toLocaleUpperCase('fr') && /\p{L}/u.test(t);

/**
 * Nom et prénom déduits de la partie locale d'une adresse, quand la ligne
 * n'en portait pas : « marie.durand@… » -> Marie DURAND.
 */
function depuisAdresse(email) {
  const local = email.split('@')[0];
  const morceaux = local.split(/[._-]+/).filter(estMot);
  if (morceaux.length >= 2) {
    return {
      prenom: capitaliser(morceaux[0]),
      nom: morceaux.slice(1).join(' ').toLocaleUpperCase('fr'),
    };
  }
  return { prenom: '', nom: (morceaux[0] || local).toLocaleUpperCase('fr') };
}

/**
 * Construit un participant à partir d'un fragment de texte contenant une
 * seule adresse.
 */
function lireSegment(segment, email) {
  let brut = segment.replace(email, ' ');

  // L'entreprise, quand la ligne la dit : une mention (« société ACME »), ou entre
  // parenthèses (« Marie DUPONT (ACME) »). Lue avant tout : les parenthèses
  // disparaissent ensuite, et ses mots ne doivent pas finir dans le nom.
  let societe = '';
  const marque = brut.match(RE_MARQUEUR_SOCIETE);
  if (marque) {
    societe = nettoyerSociete(marque[1]);
    if (societe) brut = brut.replace(marque[0], ' ');
  }
  if (!societe) {
    for (const groupe of brut.matchAll(RE_PARENTHESES)) {
      const candidat = nettoyerSociete(groupe[1]);
      // Une qualité ou une civilité entre parenthèses (« (Titulaire) ») n'est pas une entreprise.
      const estQualite =
        QUALITES.some((q) => new RegExp(echapper(q), 'i').test(candidat)) ||
        CIVILITES.some((c) => c.motif.test(candidat));
      if (candidat && !estQualite) {
        societe = candidat;
        brut = brut.replace(groupe[0], ' ');
        break;
      }
    }
  }

  // On retire les enveloppes qui entourent encore le texte (<…>, (…), […]).
  let reste = brut.replace(/[<>()[\]]/g, ' ');

  let civilite = '';
  for (const c of CIVILITES) {
    if (c.motif.test(reste)) {
      civilite = c.valeur;
      reste = reste.replace(c.motif, ' ');
      break;
    }
  }

  let qualite = '';
  for (const q of QUALITES) {
    const motif = new RegExp(echapper(q), 'i');
    if (motif.test(reste)) {
      qualite = q;
      reste = reste.replace(motif, ' ');
      break;
    }
  }

  // Le reste d'une qualité écrite en toutes lettres (« Secrétaire du CSE ») ne fait pas partie du nom.
  reste = reste.replace(/\b(?:du|de la|de l'|des|au)\s+(?:cse|comité social et économique)\b/gi, ' ').replace(/\bcse\b/gi, ' ');

  // Une colonne à part (« DUPONT Marie - ACME - … », ou séparée par une tabulation
  // ou une barre) : le premier champ est le nom, la suite l'entreprise. Le premier
  // champ doit déjà porter prénom ET nom : « DUPONT - Marie » reste un nom coupé
  // par un tiret, pas une entreprise « Marie ».
  if (!societe) {
    const champs = reste
      .split(RE_COLONNES)
      .map((c) => c.trim())
      .filter(estMot);
    if (champs.length >= 2 && champs[0].split(/\s+/).filter(estMot).length >= 2) {
      const candidat = nettoyerSociete(champs.slice(1).join(' '));
      if (candidat) {
        societe = candidat;
        reste = champs[0];
      }
    }
  }

  // Séparateurs de champs, et le tiret SEULEMENT quand il sépare des mots :
  // « Jean-Pierre » doit rester d'un bloc.
  const jetons = reste
    .split(/\s+[-–—]\s+|[,;|/]+|\s+/)
    .map((t) => t.replace(/^[^\p{L}]+|[^\p{L}.'’-]+$/gu, '').trim())
    .filter(estMot);

  let prenom = '';
  let nom = '';

  const capitales = jetons.filter(estCapitales);
  const autres = jetons.filter((t) => !estCapitales(t));

  if (capitales.length && autres.length) {
    // Forme la plus courante : « DUPONT Pierre » ou « Pierre DUPONT ».
    nom = capitales.join(' ');
    prenom = autres.map(capitaliser).join(' ');
  } else if (jetons.length >= 2) {
    // Rien en capitales : on suit l'ordre usuel, le dernier mot fait le nom.
    nom = jetons[jetons.length - 1].toLocaleUpperCase('fr');
    prenom = jetons.slice(0, -1).map(capitaliser).join(' ');
  } else if (jetons.length === 1) {
    nom = jetons[0].toLocaleUpperCase('fr');
  } else {
    ({ prenom, nom } = depuisAdresse(email));
  }

  return {
    civilite,
    prenom,
    nom,
    qualite: qualite || QUALITE_DEFAUT,
    societe,
    email: email.toLowerCase(),
    groupeLibelle: '',
  };
}

/**
 * Lit un email collé et en tire une liste de participants.
 *
 * @param {string} texte Le corps de l'email, tel qu'il a été collé.
 * @returns {Array} Participants au format du formulaire de planification.
 */
export function participantsDepuisEmail(texte) {
  const contenu = String(texte || '').replace(/\r\n?/g, '\n');
  if (!contenu.trim()) return [];

  // Une Map plutôt qu'un tableau : une adresse vue deux fois garde la
  // meilleure des deux lectures (cf. plus bas).
  const parEmail = new Map();

  for (const ligne of contenu.split('\n')) {
    const nette = ligne.replace(RE_ENTETE, '');
    const adresses = nette.match(RE_EMAIL_TOUTES);
    if (!adresses) continue;

    // Plusieurs adresses sur une ligne (un champ « À : ») : on redécoupe sur
    // les virgules et points-virgules pour rendre à chacune son nom.
    const segments =
      adresses.length > 1 ? nette.split(/[,;]+/).filter((s) => RE_EMAIL.test(s)) : [nette];

    for (const segment of segments) {
      const trouvee = segment.match(RE_EMAIL);
      if (!trouvee) continue;

      const email = trouvee[0];
      const cle = email.toLowerCase();
      const lu = lireSegment(segment, email);
      const ancien = parEmail.get(cle);

      // Un champ « À : » donne souvent l'adresse seule, la liste qui suit
      // donne le nom complet : on retient la lecture la mieux renseignée.
      if (!ancien || (!ancien.prenom && lu.prenom)) parEmail.set(cle, lu);
      // L'entreprise, elle, se complète d'une lecture à l'autre.
      const courant = parEmail.get(cle);
      if (!courant.societe && lu.societe) courant.societe = lu.societe;
      if (parEmail.size >= MAX_PARTICIPANTS) return [...parEmail.values()];
    }
  }

  return [...parEmail.values()];
}

export default participantsDepuisEmail;
